/**
 * User Management Component
 * Provides interface for viewing and managing users
 * Includes search, filtering, usage statistics, and rate limit configuration
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'user' | 'admin';
  institution: string;
  rateLimits: {
    messagesPerHour: number;
    tokensPerDay: number;
  };
  createdAt: string;
  lastLogin: string;
}

interface UserUsage {
  userId: string;
  period: {
    startDate: string;
    endDate: string;
    days: number;
  };
  usage: {
    totalMessages: number;
    totalTokens: number;
    totalCost: number;
    conversationCount: number;
  };
  breakdown: Record<string, number>;
}

interface Pagination {
  limit: number;
  offset: number;
  total: number;
  hasMore: boolean;
}

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<Pagination>({
    limit: 20,
    offset: 0,
    total: 0,
    hasMore: false,
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [institutionFilter, setInstitutionFilter] = useState<string>('');

  // Selected user for details/editing
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userUsage, setUserUsage] = useState<UserUsage | null>(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  // Rate limit editing
  const [editingRateLimits, setEditingRateLimits] = useState(false);
  const [rateLimitForm, setRateLimitForm] = useState({
    messagesPerHour: 100,
    tokensPerDay: 50000,
  });

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        limit: pagination.limit,
        offset: pagination.offset,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }
      if (roleFilter) {
        params.role = roleFilter;
      }
      if (institutionFilter) {
        params.institution = institutionFilter;
      }

      const response = await axios.get('/api/admin/users', { params });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user usage statistics
  const fetchUserUsage = async (userId: string) => {
    try {
      setLoadingUsage(true);
      const response = await axios.get(`/api/admin/users/${userId}/usage`, {
        params: { days: 30 },
      });
      setUserUsage(response.data);
    } catch (err: any) {
      console.error('Failed to load user usage:', err);
    } finally {
      setLoadingUsage(false);
    }
  };

  // Update rate limits
  const updateRateLimits = async () => {
    if (!selectedUser) return;

    try {
      await axios.put('/api/admin/rate-limits', {
        userId: selectedUser.id,
        messagesPerHour: rateLimitForm.messagesPerHour,
        tokensPerDay: rateLimitForm.tokensPerDay,
      });

      // Update local state
      setUsers(
        users.map((u) =>
          u.id === selectedUser.id
            ? {
                ...u,
                rateLimits: {
                  messagesPerHour: rateLimitForm.messagesPerHour,
                  tokensPerDay: rateLimitForm.tokensPerDay,
                },
              }
            : u
        )
      );

      setSelectedUser({
        ...selectedUser,
        rateLimits: {
          messagesPerHour: rateLimitForm.messagesPerHour,
          tokensPerDay: rateLimitForm.tokensPerDay,
        },
      });

      setEditingRateLimits(false);
      alert('Rate limits updated successfully');
    } catch (err: any) {
      alert(err.response?.data?.error?.message || 'Failed to update rate limits');
    }
  };

  // Load users on mount and when filters change
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.offset, searchQuery, roleFilter, institutionFilter]);

  // Load usage when user is selected
  useEffect(() => {
    if (selectedUser) {
      fetchUserUsage(selectedUser.id);
      setRateLimitForm({
        messagesPerHour: selectedUser.rateLimits.messagesPerHour,
        tokensPerDay: selectedUser.rateLimits.tokensPerDay,
      });
    } else {
      setUserUsage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination({ ...pagination, offset: 0 });
  };

  const handlePreviousPage = () => {
    setPagination({
      ...pagination,
      offset: Math.max(0, pagination.offset - pagination.limit),
    });
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      setPagination({
        ...pagination,
        offset: pagination.offset + pagination.limit,
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Management</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          View and manage user accounts, usage statistics, and rate limits
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <form onSubmit={handleSearch} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name or email..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* Role Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role
              </label>
              <select
                value={roleFilter}
                onChange={(e) => {
                  setRoleFilter(e.target.value);
                  setPagination({ ...pagination, offset: 0 });
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Roles</option>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Institution Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Institution
              </label>
              <input
                type="text"
                value={institutionFilter}
                onChange={(e) => {
                  setInstitutionFilter(e.target.value);
                  setPagination({ ...pagination, offset: 0 });
                }}
                placeholder="Filter by institution..."
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>

          <div className="flex justify-between items-center">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Search
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {pagination.total} total users
            </span>
          </div>
        </form>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* User List and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User List */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Users</h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">No users found</p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => setSelectedUser(user)}
                    className={`p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedUser?.id === user.id
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-600'
                        : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                          {user.name}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {user.email}
                        </p>
                        <div className="mt-1 flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {user.role}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            {user.institution}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <button
                  onClick={handlePreviousPage}
                  disabled={pagination.offset === 0}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of{' '}
                  {pagination.total}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={!pagination.hasMore}
                  className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>

        {/* User Details */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {selectedUser ? (
            <>
              <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">User Details</h3>
              </div>

              <div className="p-4 space-y-6">
                {/* Basic Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Basic Information
                  </h4>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400">Name:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedUser.name}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400">Email:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedUser.email}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400">Role:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedUser.role}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400">Institution:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {selectedUser.institution}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400">Created:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {new Date(selectedUser.createdAt).toLocaleDateString()}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600 dark:text-gray-400">Last Login:</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-white">
                        {new Date(selectedUser.lastLogin).toLocaleDateString()}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Rate Limits */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Rate Limits
                    </h4>
                    {!editingRateLimits && (
                      <button
                        onClick={() => setEditingRateLimits(true)}
                        className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  {editingRateLimits ? (
                    <div className="space-y-3">
                      <div>
                        <label htmlFor="messagesPerHour" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Messages per Hour
                        </label>
                        <input
                          id="messagesPerHour"
                          type="number"
                          value={rateLimitForm.messagesPerHour}
                          onChange={(e) =>
                            setRateLimitForm({
                              ...rateLimitForm,
                              messagesPerHour: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div>
                        <label htmlFor="tokensPerDay" className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
                          Tokens per Day
                        </label>
                        <input
                          id="tokensPerDay"
                          type="number"
                          value={rateLimitForm.tokensPerDay}
                          onChange={(e) =>
                            setRateLimitForm({
                              ...rateLimitForm,
                              tokensPerDay: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={updateRateLimits}
                          className="flex-1 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => {
                            setEditingRateLimits(false);
                            setRateLimitForm({
                              messagesPerHour: selectedUser.rateLimits.messagesPerHour,
                              tokensPerDay: selectedUser.rateLimits.tokensPerDay,
                            });
                          }}
                          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600 dark:text-gray-400">Messages/Hour:</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white">
                          {selectedUser.rateLimits.messagesPerHour}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600 dark:text-gray-400">Tokens/Day:</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white">
                          {selectedUser.rateLimits.tokensPerDay.toLocaleString()}
                        </dd>
                      </div>
                    </dl>
                  )}
                </div>

                {/* Usage Statistics */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Usage Statistics (Last 30 Days)
                  </h4>

                  {loadingUsage ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : userUsage?.usage ? (
                    <dl className="space-y-2">
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600 dark:text-gray-400">Total Messages:</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white">
                          {userUsage.usage.totalMessages}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600 dark:text-gray-400">Total Tokens:</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white">
                          {userUsage.usage.totalTokens.toLocaleString()}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600 dark:text-gray-400">Total Cost:</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white">
                          ${userUsage.usage.totalCost.toFixed(2)}
                        </dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600 dark:text-gray-400">Conversations:</dt>
                        <dd className="text-sm font-medium text-gray-900 dark:text-white">
                          {userUsage.usage.conversationCount}
                        </dd>
                      </div>
                    </dl>
                  ) : (
                    <p className="text-sm text-gray-600 dark:text-gray-400">No usage data available</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="p-8 text-center">
              <p className="text-gray-600 dark:text-gray-400">Select a user to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
